import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/shared/sheet';
import { Slider } from '@/components/shared/slider';
import { Badge } from '@/components/shared/badge';
import { useAssessment } from '@/contexts/AssessmentContext';
import { OpportunityScore } from '@/types/opportunity';
import { calculateScores } from '@/utils/scoringUtils';

interface AboutTheScoreSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AboutTheScoreSidebar = React.memo(({ isOpen, onClose }: AboutTheScoreSidebarProps) => {
  const { scoringParams, setScoringParams, recalculateScores, weights, opportunities, scoredOpportunities } = useAssessment();
  
  // Local state for slider to provide a smooth experience during drag
  const [localAlpha, setLocalAlpha] = useState(scoringParams.alpha ?? 0.5);
  
  // Refs for intelligent drag detection
  const sliderRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const lastKnownAlphaRef = useRef(localAlpha);
  
  // Sync local state with external state only when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setLocalAlpha(scoringParams.alpha ?? 0.5);
      lastKnownAlphaRef.current = scoringParams.alpha ?? 0.5;
    }
  }, [isOpen, scoringParams.alpha]); // Note: syncs on open and when scoringParams.alpha changes
  
  const alpha = localAlpha;

  // Helper function to clamp alpha value to valid range
  const clampAlpha = useCallback((value: number): number => {
    return Math.max(0, Math.min(1, value));
  }, []);

  // Intelligent drag detection to handle out-of-bounds dragging
  const handleGlobalPointerMove = useCallback((event: PointerEvent) => {
    if (!isDraggingRef.current || !sliderRef.current) return;
    
    const sliderRect = sliderRef.current.getBoundingClientRect();
    const pointerX = event.clientX;
    
    // Calculate the relative position within the slider
    const relativeX = (pointerX - sliderRect.left) / sliderRect.width;
    
    // If dragging beyond bounds, force to extreme values
    if (relativeX < 0) {
      // Dragging left past the slider - force to 0
      setLocalAlpha(0);
      lastKnownAlphaRef.current = 0;
      setScoringParams({ alpha: 0 });
      recalculateScores();
    } else if (relativeX > 1) {
      // Dragging right past the slider - force to 1
      setLocalAlpha(1);
      lastKnownAlphaRef.current = 1;
      setScoringParams({ alpha: 1 });
      recalculateScores();
    }
  }, [setScoringParams, recalculateScores]);

  const handleGlobalPointerUp = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      // No need to commit the final value since we're already updating in real-time
      // Just mark that dragging has ended
    }
  }, []);

  // Set up global event listeners for intelligent drag detection
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('pointermove', handleGlobalPointerMove);
      document.addEventListener('pointerup', handleGlobalPointerUp);
      document.addEventListener('pointercancel', handleGlobalPointerUp);
      
      return () => {
        document.removeEventListener('pointermove', handleGlobalPointerMove);
        document.removeEventListener('pointerup', handleGlobalPointerUp);
        document.removeEventListener('pointercancel', handleGlobalPointerUp);
      };
    }
  }, [isOpen, handleGlobalPointerMove, handleGlobalPointerUp]);

  // Handler for slider value changes during dragging - only updates local state for UI responsiveness
  const handleAlphaChange = useCallback((value: number[]) => {
    const clampedValue = clampAlpha(value[0]);
    setLocalAlpha(clampedValue);
    lastKnownAlphaRef.current = clampedValue;
    
    // Immediately update the scoring params and recalculate scores for real-time feedback
    setScoringParams({ alpha: clampedValue });
    recalculateScores();
  }, [clampAlpha, setScoringParams, recalculateScores]);

  // Handler for when user starts dragging
  const handleSliderPointerDown = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  // Handler for when user finishes interacting with the slider (mouse up, key up, etc.)
  const handleAlphaCommit = useCallback((value: number[]) => {
    const clampedValue = clampAlpha(value[0]);
    // Scores are already updated in real-time, so we just need to ensure the final state is correct
    setLocalAlpha(clampedValue);
    lastKnownAlphaRef.current = clampedValue;
    isDraggingRef.current = false;
  }, [clampAlpha]);

  // Memoized close handler to prevent unnecessary re-renders
  const memoizedOnClose = useCallback(() => {
    // Since we're updating scores in real-time, the current state should already be correct
    // Just close the sidebar
    onClose();
  }, [onClose]);

  // Memoize scored opportunities to prevent unnecessary re-renders
  // Only update when sidebar opens or when we actually need the data
  const memoizedScoredOpportunities = useMemo(() => {
    return isOpen ? scoredOpportunities : [];
  }, [isOpen, scoredOpportunities]);

  // Get an example opportunity for demonstrating the impact
  const exampleOpportunity = memoizedScoredOpportunities.length > 0 ? memoizedScoredOpportunities[0] : null;

  
  // Calculate what the example opportunity's score would be with different alpha values
  const exampleAlphaComparison = useMemo(() => {
    if (!exampleOpportunity || !opportunities) return null;
    
    const originalOpp = opportunities.find(opp => opp.Name === exampleOpportunity.Name);
    if (!originalOpp) return null;
    
    const alphaValues = [0.2, 0.5, 0.8];
    return alphaValues.map(alphaVal => {
      const scores = calculateScores([originalOpp], weights, { alpha: alphaVal });
      return {
        alpha: alphaVal,
        finalScore: scores[0]?.finalScore || 0
      };
    });
  }, [exampleOpportunity, opportunities, weights]);

  const valueDimensions = [
    { key: 'SpeedUpPermitting', label: 'Speed Up Permitting' },
    { key: 'IncreaseStaffProductivity', label: 'Increase Staff Productivity' },
    { key: 'SimplifyApplicationExperience', label: 'Simplify Application Experience' },
    { key: 'EnhanceEquitableAccessToServices', label: 'Enhance Equitable Access' },
    { key: 'StrengthenInternalVisibilityDecisionMaking', label: 'Strengthen Decision Making' },
    { key: 'IncreaseProcessTransparency', label: 'Increase Process Transparency' },
    { key: 'BoostFinancialSustainability', label: 'Boost Financial Sustainability' }
  ];

  const challengeFactors = [
    { key: 'CostAndEffort', label: 'Cost and Effort' },
    { key: 'Risk', label: 'Risk' },
    { key: 'Complexity', label: 'Complexity' }
  ];

  // Only render the sheet content when the sidebar is open to prevent unnecessary DOM updates
  if (!isOpen) {
    return null;
  }

  return (
    <Sheet open={isOpen} onOpenChange={memoizedOnClose}>
      <SheetContent 
        side="right" 
        className="w-full sm:w-[33vw] sm:max-w-none overflow-y-auto px-6 py-6"
      >
        <div className="space-y-6">
          {/* Title matching AboutTheData styling */}
          <div>
            <h2 className="text-3xl font-semibold tracking-tight text-neutral-900 mb-5">About the Score</h2>
            <p className="text-base leading-7 text-neutral-700 mb-6">
              Understand how opportunity scores are calculated and customize the balance between value and feasibility.
            </p>
          </div>

          {/* Divider */}
          <div className="h-px bg-neutral-200" />

          {/* How We Calculate Scores Section */}
          <section>
            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-2">How We Calculate Scores</h3>
            <div className="space-y-4">
              <p className="text-base leading-7 text-neutral-700">
                We calculate the score by combining <strong>Value</strong> and <strong>Feasibility</strong> using a weighted geometric mean. 
                This method rewards balanced opportunities — both high value and realistically achievable.
              </p>
              <div className="bg-white ring-1 ring-neutral-200 rounded-xl p-5">
                <p className="font-mono text-sm text-neutral-800">
                  <strong>Formula:</strong> FinalScore = Value<sup className="relative -top-2 text-xs text-[#CA0079]">α</sup> × Feasibility<sup className="relative -top-1 text-xs text-[#CA0079]">(1-α)</sup>
                </p>
              </div>
            </div>
          </section>

          {/* Divider */}
          <div className="h-px bg-neutral-200" />

          {/* Balance Section */}
          <section>
            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-2">Balance between Value and Feasibility</h3>
            <div className="space-y-4">
              <p className="text-base leading-7 text-neutral-700">
                Adjust the balance to match your organization's priorities. Move the slider to emphasize either feasibility (easier to implement) or value (bigger potential wins).
              </p>
              <div className="bg-white ring-1 ring-neutral-200 rounded-xl p-5">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-neutral-600">Current Balance (α = {alpha.toFixed(2)})</span>
                  <Badge variant="outline" className="text-xs">
                    {alpha < 0.05 ? 'Feasibility Only' : alpha > 0.95 ? 'Value Only' : alpha < 0.4 ? 'Favors Feasibility' : alpha > 0.6 ? 'Favors Value' : 'Balanced'}
                  </Badge>
                </div>
                <div ref={sliderRef}>
                  <Slider
                    value={[alpha]}
                    onValueChange={handleAlphaChange}
                    onValueCommit={handleAlphaCommit}
                    onPointerDown={handleSliderPointerDown}
                    min={0}
                    max={1}
                    step={0.01}
                    className="w-full"
                  />
                </div>
                <div className="flex justify-between text-sm text-neutral-600 mt-2">
                  <span>Easier to Implement</span>
                  <span>Bigger Potential Wins</span>
                </div>
              </div>
            </div>
          </section>

          {/* Divider */}
          <div className="h-px bg-neutral-200" />

          {/* Why This Approach Section */}
          <section>
            <h3 className="text-xl font-semibold text-neutral-900 mt-6 mb-2">Why This Approach?</h3>
            <div className="space-y-4">
              <p className="text-base leading-7 text-neutral-700">
                <strong>Geometric Mean:</strong> Prevents extremely high scores when one dimension is very high but the other is very low.
                This encourages balanced opportunities.
              </p>
              <p className="text-base leading-7 text-neutral-700">
                <strong>Multiplicative Challenges:</strong> A single major challenge (like very high cost) significantly impacts feasibility,
                reflecting real-world constraints.
              </p>
              <p className="text-base leading-7 text-neutral-700">
                <strong>Transparency:</strong> You can see exactly how each factor contributes to the final score and adjust the balance
                to match your organization's priorities.
              </p>
            </div>
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if isOpen changes
  // Ignore all other prop changes to maintain complete isolation
  return prevProps.isOpen === nextProps.isOpen;
});

AboutTheScoreSidebar.displayName = 'AboutTheScoreSidebar';

export default AboutTheScoreSidebar;
